<script setup lang="ts">
import { trimmedMaybe } from "../../lib/trimmedString";
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../../lib/maybe";
import {
  computed,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  type Ref,
} from "vue";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import { openContextMenu } from "../../lib/platform/contextMenu";
import {
  displayFileName,
  hasMapExtension,
  documentFileName,
} from "../../lib/fileNames";
import { DATABASE_ENTITY_TYPES } from "../../lib/entityFrontmatter";
import { clampMapDimension, MAP_PAGE_DEFAULT } from "../../lib/mapPage";
import { isMapFileName } from "../../lib/mapDocument";
import { projectQuery } from "../../lib/projectRouter";
import { useEditorSessionStore } from "../../stores/useEditorSessionStore";
import { useOutlineActionsStore } from "../../stores/useOutlineActionsStore";
import { useProjectWorkspaceStore } from "../../stores/useProjectWorkspaceStore";
import { useOutlineEntitiesStore } from "../../stores/useOutlineEntitiesStore";
import { useCreateEntityFlow } from "../../composables/useCreateEntityFlow";
import { useRenameEntityFlow } from "../../composables/useRenameEntityFlow";
import MapPageSizeFields from "../MapPageSizeFields.vue";
import ZwButton from "../ui/ZwButton.vue";
import ZwCheckbox from "../ui/ZwCheckbox.vue";
import ZwDialog from "../ui/ZwDialog.vue";
import ZwInput from "../ui/ZwInput.vue";
import ZwMenu from "../ui/ZwMenu.vue";
import ZwMenuSeparator from "../ui/ZwMenuSeparator.vue";
import ZwSelect from "../ui/ZwSelect.vue";
import { IconFilter } from "../ribbonIcons";

type FocusableInput = {
  focus: () => void;
  select: () => void;
};

function focusableInputRef(raw: {
  readonly value: unknown;
}): Ref<Maybe<FocusableInput>> {
  return computed(() =>
    toMaybe(raw.value as InstanceType<typeof ZwInput>),
  ) as Ref<Maybe<FocusableInput>>;
}

const emit = defineEmits<{ requestCloseSettings: [] }>();

const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const entities = useOutlineEntitiesStore();
const outlineActions = useOutlineActionsStore();
const route = useRoute();
const router = useRouter();
const { activeDatabase } = storeToRefs(session);
const { databaseEntries } = storeToRefs(entities);

function isDatabaseActive(name: string): boolean {
  return matchMaybe(activeDatabase.value, {
    just: (active) => active === name,
    nothing: () => false,
  });
}

const fieldsByName = computed(() => {
  const map = new Map<string, (typeof entities.databaseEntryFields)[number]>();
  for (const summary of entities.databaseEntryFields)
    map.set(summary.name, summary);
  return map;
});

/** Only types actually present get a filter option — no point offering
 * "Faction" when nothing in this project is typed that way yet. */
const availableTypes = computed(() => {
  const present = new Set<string>();
  for (const summary of entities.databaseEntryFields) {
    const type = trimmedMaybe(fromMaybe(summary.fields.type));
    if (isJust(type)) present.add(type.value);
  }
  return DATABASE_ENTITY_TYPES.filter((t) => present.has(t.id));
});

const availableTags = computed(() => {
  const present = new Set<string>();
  for (const summary of entities.databaseEntryFields) {
    for (const tag of summary.fields.tags) present.add(tag);
  }
  return [...present].sort((a, b) => a.localeCompare(b));
});

const typeFilter = ref<Maybe<string>>(nothing);
const tagFilters = ref<Set<string>>(new Set());

function toggleTagFilter(tag: string): void {
  const next = new Set(tagFilters.value);
  if (next.has(tag)) next.delete(tag);
  else next.add(tag);
  tagFilters.value = next;
}

const hasActiveFilters = computed(
  () => isJust(typeFilter.value) || tagFilters.value.size > 0,
);

const typeLabelById = computed(() => {
  const map = new Map<string, string>();
  for (const type of DATABASE_ENTITY_TYPES) map.set(type.id, type.label);
  return map;
});

/** Label for the sidecar badge — null for maps (which get their own "Map"
 * badge) and untyped entries (nothing to show). */
function entryTypeLabel(name: string): Maybe<string> {
  const summary = toMaybe(fieldsByName.value.get(name));
  if (isNothing(summary) || summary.value.isMap) return nothing;
  const type = trimmedMaybe(fromMaybe(summary.value.fields.type));
  if (isNothing(type)) return nothing;
  return just(typeLabelById.value.get(type.value) ?? type.value);
}

function typeFilterMatches(id: string): boolean {
  return isJust(typeFilter.value) && typeFilter.value.value === id;
}

function setTypeFilter(id: string, active: boolean): void {
  typeFilter.value = active ? just(id) : nothing;
}

/** Drives the pills shown next to the Filter button — one per active
 * filter, each clearing only itself when clicked. */
const activeFilterPills = computed(() => {
  const pills: { key: string; label: string; clear: () => void }[] = [];
  if (isJust(typeFilter.value)) {
    const type = typeFilter.value.value;
    pills.push({
      key: `type:${type}`,
      label: typeLabelById.value.get(type) ?? type,
      clear: () => {
        typeFilter.value = nothing;
      },
    });
  }
  for (const tag of tagFilters.value) {
    pills.push({
      key: `tag:${tag}`,
      label: tag,
      clear: () => toggleTagFilter(tag),
    });
  }
  return pills;
});

/** Filterable rows only — maps have no typed fields, so they're excluded the
 * moment any filter is active rather than silently always showing. */
const visibleEntries = computed(() => {
  if (hasActiveFilters.value === false) return entities.databaseEntries;
  return entities.databaseEntries.filter((name) => {
    const summary = toMaybe(fieldsByName.value.get(name));
    if (isNothing(summary) || summary.value.isMap) return false;
    if (isJust(typeFilter.value)) {
      const selectedType = fromMaybe(summary.value.fields.type);
      if (selectedType !== typeFilter.value.value) return false;
    }
    if (tagFilters.value.size > 0) {
      const hasAnySelectedTag = summary.value.fields.tags.some((t) =>
        tagFilters.value.has(t),
      );
      if (hasAnySelectedTag === false) return false;
    }
    return true;
  });
});

function closeMobileOutline(): void {
  if (session.mobileShell) {
    session.outlineOpen = false;
  }
}

function onSelectDatabase(name: string): void {
  databaseRename.cancel();
  void workspace.onSelectProjectDatabase(name);
  closeMobileOutline();
}

const mapWidth = ref(MAP_PAGE_DEFAULT.width);
const mapHeight = ref(MAP_PAGE_DEFAULT.height);
const databaseKind = ref<"entry" | "map">("entry");
const databaseInput =
  useTemplateRef<InstanceType<typeof ZwInput>>("databaseInput");

const databaseFlow = useCreateEntityFlow({
  inputRef: focusableInputRef(databaseInput),
  existingNames: databaseEntries,
  normalizeName: (raw) =>
    databaseKind.value === "map"
      ? hasMapExtension(raw)
        ? raw
        : `${raw}.zwmap`
      : documentFileName(raw),
  emptyNameError: "Enter an entry name.",
  duplicateError: "That entry already exists.",
  onCreate: async (finalName) => {
    await workspace.onNewProjectDatabase(finalName, {
      kind: databaseKind.value,
      mapPage:
        databaseKind.value === "map"
          ? just({
              width: clampMapDimension(mapWidth.value),
              height: clampMapDimension(mapHeight.value),
              frame: "none",
            })
          : nothing,
    });
    void entities.refreshDatabase();
  },
});

function onNewDatabase(): void {
  emit("requestCloseSettings");
  databaseKind.value = "entry";
  databaseFlow.start();
}

async function onDatabaseContextMenu(
  event: MouseEvent,
  name: string,
): Promise<void> {
  emit("requestCloseSettings");
  databaseFlow.cancel();

  await openContextMenu(event, [
    {
      id: `rename-database-${name}`,
      label: "Rename",
      action: () => {
        void databaseRename.start(name, displayFileName(name));
      },
    },
    {
      id: `delete-database-${name}`,
      label: "Delete",
      action: () => {
        void (async () => {
          await workspace.onDeleteProjectDatabase(name);
          await entities.refreshDatabase();
        })();
      },
    },
  ]);
}

const renameDatabaseInput = useTemplateRef<InstanceType<typeof ZwInput>>(
  "renameDatabaseInput",
);
// Keeping the entry's `name` frontmatter field in sync with its filename
// (#72's "changing one changes the other") happens atomically inside
// `rename_project_database_entry` itself now — a separate frontend
// read-modify-write here used to race whatever reactively reloads this
// entry's fields the moment the rename lands (e.g. `EntityFieldsPanel.vue`),
// sometimes leaving the pre-rename name showing.
const databaseRename = useRenameEntityFlow<string>(
  focusableInputRef(renameDatabaseInput),
  {
    allowEmpty: false,
    onRename: (from, to) => workspace.onRenameProjectDatabase(from, to),
    afterCommit: just(() => {
      void entities.refreshDatabase();
    }),
  },
);

function isDatabaseRenaming(name: string): boolean {
  return (
    isJust(databaseRename.renaming.value) &&
    databaseRename.renaming.value.value === name
  );
}

function registerOutlineChrome(): void {
  outlineActions.headerNew = just({
    label: "New worldbuilding entry",
    run: onNewDatabase,
    items: nothing,
  });
  outlineActions.cancelCreating = just(() => databaseFlow.cancel());
}

function clearOutlineChrome(): void {
  outlineActions.headerNew = nothing;
  outlineActions.cancelCreating = nothing;
}

onMounted(registerOutlineChrome);
onActivated(registerOutlineChrome);
onDeactivated(clearOutlineChrome);
onUnmounted(clearOutlineChrome);
</script>

<template>
  <nav
    class="zw-outline-nav zw-scroll zw-outline-nav-database"
    aria-label="Worldbuilding"
  >
    <div
      class="zw-outline-database-view-toggle"
      role="group"
      aria-label="Database view"
    >
      <button
        type="button"
        class="zw-outline-database-view-btn"
        :class="{
          'zw-outline-database-view-btn-active':
            route.name !== 'database-graph',
        }"
        @click="void router.push({ path: '/database', query: projectQuery() })"
      >
        Entries
      </button>
      <button
        type="button"
        class="zw-outline-database-view-btn"
        :class="{
          'zw-outline-database-view-btn-active':
            route.name === 'database-graph',
        }"
        :disabled="
          entities.databaseEntryFields.some((e) => e.isMap === false) === false
        "
        @click="
          void router.push({ path: '/database/graph', query: projectQuery() })
        "
      >
        Graph
      </button>
    </div>
    <div v-if="entities.databaseEntries.length === 0" class="zw-outline-empty">
      <p>No worldbuilding entries yet.</p>
      <ZwButton size="sm" variant="primary" @click="onNewDatabase">
        New entry
      </ZwButton>
    </div>
    <template v-else>
      <div
        v-if="availableTypes.length > 0 || availableTags.length > 0"
        class="zw-outline-database-filters"
      >
        <ZwMenu
          align="start"
          class="zw-outline-filter-menu"
          :menu-label="nothing"
        >
          <template #trigger="{ toggle, attrs }">
            <button
              type="button"
              class="zw-outline-filter-trigger"
              v-bind="attrs"
              @click="toggle()"
            >
              <IconFilter class="size-3.5" aria-hidden="true" />
              Filter
            </button>
          </template>
          <div
            v-if="availableTypes.length > 0"
            class="zw-outline-filter-menu-section"
          >
            <p class="zw-outline-filter-menu-heading">Type</p>
            <ZwCheckbox
              v-for="type in availableTypes"
              :key="type.id"
              class="zw-outline-filter-menu-option"
              :model-value="typeFilterMatches(type.id)"
              @update:model-value="setTypeFilter(type.id, $event)"
            >
              {{ type.label }}
            </ZwCheckbox>
          </div>
          <ZwMenuSeparator
            v-if="availableTypes.length > 0 && availableTags.length > 0"
          />
          <div
            v-if="availableTags.length > 0"
            class="zw-outline-filter-menu-section"
          >
            <p class="zw-outline-filter-menu-heading">Tags</p>
            <ZwCheckbox
              v-for="tag in availableTags"
              :key="tag"
              class="zw-outline-filter-menu-option"
              :model-value="tagFilters.has(tag)"
              @update:model-value="toggleTagFilter(tag)"
            >
              {{ tag }}
            </ZwCheckbox>
          </div>
        </ZwMenu>
        <div v-if="hasActiveFilters" class="zw-outline-filter-pills">
          <button
            v-for="pill in activeFilterPills"
            :key="pill.key"
            type="button"
            class="zw-outline-filter-pill"
            :aria-label="`Clear filter: ${pill.label}`"
            @click="pill.clear"
          >
            {{ pill.label }}
            <span class="zw-outline-filter-pill-remove" aria-hidden="true">
              ×
            </span>
          </button>
        </div>
      </div>
      <p
        v-if="hasActiveFilters && visibleEntries.length === 0"
        class="zw-outline-empty"
      >
        No entries match these filters.
      </p>
      <ul v-else class="sidecar-list">
        <li
          v-for="name in visibleEntries"
          :key="name"
          class="sidecar-item"
          :class="{ 'sidecar-item-active': isDatabaseActive(name) }"
          @contextmenu="onDatabaseContextMenu($event, name)"
        >
          <ZwInput
            v-if="isDatabaseRenaming(name)"
            :id="nothing"
            ref="renameDatabaseInput"
            v-model="databaseRename.draft.value"
            class="file-rename"
            aria-label="Rename worldbuilding entry"
            :label="nothing"
            @keydown.enter.prevent="databaseRename.commit"
            @keydown.escape.prevent="databaseRename.cancel"
            @blur="databaseRename.commit"
          />
          <button
            v-else
            type="button"
            class="sidecar-link"
            :title="displayFileName(name)"
            @click="onSelectDatabase(name)"
            @dblclick.stop.prevent="
              databaseRename.start(name, displayFileName(name))
            "
          >
            {{ displayFileName(name) }}
            <span
              v-if="isMapFileName(name) || isJust(entryTypeLabel(name))"
              class="sidecar-badge"
              aria-hidden="true"
            >
              <template v-if="isMapFileName(name)">Map</template>
              <template v-else>{{ fromMaybe(entryTypeLabel(name)) }}</template>
            </span>
          </button>
        </li>
      </ul>
    </template>
  </nav>

  <ZwDialog
    :open="databaseFlow.open.value"
    title="New worldbuilding entry"
    :description="nothing"
    @close="databaseFlow.cancel"
  >
    <ZwSelect
      :id="just('worldbuilding-kind')"
      v-model="databaseKind"
      :label="just('Kind')"
      :options="
        just([
          { value: 'entry', label: 'Entry (character, location, etc)' },
          { value: 'map', label: 'Map (sketch)' },
        ])
      "
    />
    <ZwInput
      :id="nothing"
      ref="databaseInput"
      v-model="databaseFlow.draft.value"
      :label="just('Name')"
      :placeholder="databaseKind === 'map' ? 'Map name' : 'Entry name'"
      @keydown.enter.prevent="databaseFlow.commit"
    />
    <MapPageSizeFields
      v-if="databaseKind === 'map'"
      v-model:width="mapWidth"
      v-model:height="mapHeight"
    />

    <p v-if="databaseFlow.error.value.length > 0" class="zw-dialog-error">
      {{ databaseFlow.error.value }}
    </p>
    <template #footer>
      <ZwButton @click="databaseFlow.cancel">Cancel</ZwButton>
      <ZwButton variant="primary" @click="databaseFlow.commit">Create</ZwButton>
    </template>
  </ZwDialog>
</template>

<style scoped>
.zw-outline-nav {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 var(--space-sm) var(--space-md);
}

.zw-outline-nav-database {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.zw-outline-database-view-toggle {
  display: flex;
  align-items: stretch;
  margin: 0 var(--space-sm);
  border: 1px solid var(--line);
  border-radius: 0.35rem;
  overflow: hidden;
  background: var(--bg);
}

.zw-outline-database-view-btn {
  appearance: none;
  border: 0;
  background: transparent;
  flex: 1;
  padding: 0.35rem 0.5rem;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
  cursor: pointer;
}

.zw-outline-database-view-btn + .zw-outline-database-view-btn {
  border-left: 1px solid var(--line);
}

.zw-outline-database-view-btn:hover:not(:disabled) {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.zw-outline-database-view-btn-active {
  color: var(--text-strong);
  background: color-mix(in oklch, var(--color-zw-amber) 16%, transparent);
}

.zw-outline-database-view-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.zw-outline-empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-sm);
  margin: var(--space-sm) var(--space-sm) 0;
  font-size: 0.9rem;
  line-height: 1.4;
  color: color-mix(in oklch, var(--text-muted) 85%, transparent);
}

.zw-outline-empty p {
  margin: 0;
}

.sidecar-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.sidecar-item {
  border-radius: 0.25rem;
}

.sidecar-item-active {
  background: color-mix(in oklch, var(--color-zw-amber) 20%, transparent);
}

.sidecar-link {
  appearance: none;
  border: 0;
  background: transparent;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 0.28rem 0.45rem;
  text-align: left;
  font: inherit;
  font-size: 0.92rem;
  color: var(--text);
  cursor: pointer;
  border-radius: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidecar-link:hover {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.sidecar-item-active .sidecar-link {
  color: var(--text-strong);
}

.sidecar-badge {
  display: inline-block;
  margin-right: 0.35rem;
  padding: 0.05rem 0.3rem;
  border: 1px solid var(--line);
  border-radius: 0.2rem;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
  vertical-align: 0.05em;
}

.zw-outline-database-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  margin: 0 0.35rem 0.5rem;
}

.zw-outline-filter-trigger {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid var(--line);
  background: transparent;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font: inherit;
  font-size: 0.75rem;
  color: var(--text-muted);
  cursor: pointer;
  transition:
    color var(--duration-fast) var(--ease-out-quart),
    background-color var(--duration-fast) var(--ease-out-quart);
}

.zw-outline-filter-trigger:hover {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.zw-outline-filter-menu-section {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.2rem 0.3rem;
}

.zw-outline-filter-menu-heading {
  margin: 0.1rem 0.15rem 0.15rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.zw-outline-filter-menu-option {
  padding: 0.3rem 0.45rem;
  border-radius: 0.25rem;
}

.zw-outline-filter-menu-option:hover {
  background: var(--hover-fill);
}

.zw-outline-filter-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.zw-outline-filter-pill {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border: 0;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
  background: color-mix(in oklch, var(--color-zw-amber) 20%, transparent);
  font-size: 0.75rem;
  color: var(--text-strong);
  cursor: pointer;
}

.zw-outline-filter-pill:hover {
  background: color-mix(in oklch, var(--color-zw-amber) 30%, transparent);
}

.zw-outline-filter-pill-remove {
  font-size: 0.9rem;
  line-height: 1;
  color: var(--text-muted);
}

.zw-outline-filter-pill:hover .zw-outline-filter-pill-remove {
  color: var(--text-strong);
}

.zw-outline-template-hint {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.4;
  color: var(--text-muted);
}

.zw-outline-template-hint code {
  font-family: var(--font-mono);
  font-size: 0.85em;
}
</style>
